/* ============================================================
   JT-R GARAGE — reactive motion system
   Lightweight, dependency-free and Shopify-editor friendly.
   - cinematic hero entrance + mouse/scroll parallax
   - reveal-on-scroll + staggered groups
   - animated heading accents
   - nav scroll progress + compact/hide-on-down behaviour
   - reactive card lighting + premium tilt
   - magnetic primary interactions
   - subtle image parallax
   - chassis filter + product-card image cycle (preserved)
   - reduced-motion + touch fallbacks
   ============================================================ */
(function () {
  'use strict';

  var doc = document;
  var win = window;
  var reduceMotion = !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var finePointer = !!(win.matchMedia && win.matchMedia('(hover: hover) and (pointer: fine)').matches);
  var revealObserver = null;

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function markReady() {
    if (!doc.body) return;
    win.requestAnimationFrame(function () {
      doc.body.classList.add('jtr-ready');
    });
  }

  /* ---------- reveal on scroll + stagger ---------- */
  function ensureRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in win) || reduceMotion) return;

    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('jtr-in');
        revealObserver.unobserve(entry.target);
      });
    }, {
      threshold: 0.09,
      rootMargin: '0px 0px -7% 0px'
    });
  }

  function prepareRevealGroup(parent) {
    if (!parent || parent.getAttribute('data-jtr-stagger-ready') === 'true') return;
    parent.setAttribute('data-jtr-stagger-ready', 'true');

    var children = toArray(parent.children).filter(function (child) {
      return !child.classList.contains('visually-hidden');
    });

    children.forEach(function (child, index) {
      if (!child.classList.contains('jtr-reveal')) child.classList.add('jtr-auto-reveal');
      child.style.setProperty('--jtr-delay', (Math.min(index, 5) * 80) + 'ms');
    });
  }

  function initReveals(scope) {
    scope = scope || doc;

    toArray(scope.querySelectorAll('.jtr-grid-4, .jtr-grid-3, .jtr-two-up, .jtr-social-video-grid, .jtr-footer-grid, .jtr-newsletter-grid, .jtr-about-grid, [class*="ai-social-follow-bar__grid"], [class*="ai-about-section-grid"]')).forEach(prepareRevealGroup);

    toArray(scope.querySelectorAll('.jtr-h2')).forEach(function (heading) {
      heading.classList.add('jtr-heading-motion');
      if (!heading.closest('#top') && !heading.classList.contains('jtr-reveal')) {
        heading.classList.add('jtr-auto-reveal');
      }
    });

    toArray(scope.querySelectorAll('.jtr-eyebrow')).forEach(function (eyebrow) {
      if (!eyebrow.closest('#top') && !eyebrow.classList.contains('jtr-reveal')) {
        eyebrow.classList.add('jtr-auto-reveal', 'jtr-reveal-soft');
      }
    });

    var items = toArray(scope.querySelectorAll('.jtr-reveal, .jtr-auto-reveal'));

    if (reduceMotion) {
      items.forEach(function (item) { item.classList.add('jtr-in'); });
      return;
    }

    ensureRevealObserver();

    if (revealObserver) {
      items.forEach(function (item) {
        if (item.getAttribute('data-jtr-observed') === 'true') return;
        item.setAttribute('data-jtr-observed', 'true');
        revealObserver.observe(item);
      });
    } else {
      items.forEach(function (item) { item.classList.add('jtr-in'); });
    }
  }

  /* ---------- reactive pointer glow ---------- */
  function initPointerGlow() {
    if (!finePointer || reduceMotion || doc.querySelector('.jtr-pointer-glow')) return;

    var glow = doc.createElement('div');
    glow.className = 'jtr-pointer-glow';
    glow.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(glow);

    var tx = win.innerWidth * 0.5;
    var ty = win.innerHeight * 0.5;
    var x = tx;
    var y = ty;

    win.addEventListener('pointermove', function (event) {
      tx = event.clientX;
      ty = event.clientY;
      glow.classList.add('is-active');
    }, { passive: true });

    doc.addEventListener('mouseleave', function () {
      glow.classList.remove('is-active');
    });

    function glowTick() {
      x += (tx - x) * 0.16;
      y += (ty - y) * 0.16;
      glow.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0) translate(-50%,-50%)';
      win.requestAnimationFrame(glowTick);
    }

    win.requestAnimationFrame(glowTick);
  }

  /* ---------- reactive card lighting ---------- */
  function initReactiveCards(scope) {
    if (!finePointer || reduceMotion) return;
    scope = scope || doc;

    toArray(scope.querySelectorAll('.jtr-product-card-premium, .jtr-feat, .jtr-social-video-card, [class*="ai-social-follow-bar__item"]')).forEach(function (card) {
      if (card.getAttribute('data-jtr-reactive-ready') === 'true') return;
      card.setAttribute('data-jtr-reactive-ready', 'true');
      card.classList.add('jtr-reactive-card');

      card.addEventListener('pointermove', function (event) {
        var rect = card.getBoundingClientRect();
        card.style.setProperty('--jtr-mx', (event.clientX - rect.left).toFixed(1) + 'px');
        card.style.setProperty('--jtr-my', (event.clientY - rect.top).toFixed(1) + 'px');
      }, { passive: true });
    });
  }

  /* ---------- magnetic CTAs ---------- */
  function initMagnetic(scope) {
    if (!finePointer || reduceMotion) return;
    scope = scope || doc;

    toArray(scope.querySelectorAll('.jtr-btn-fill, .jtr-btn-ghost, .jtr-btn, .jtr-social-btn, .jtr-collection-tab')).forEach(function (el) {
      if (el.getAttribute('data-jtr-magnetic-ready') === 'true') return;
      el.setAttribute('data-jtr-magnetic-ready', 'true');
      el.classList.add('jtr-magnetic');

      el.addEventListener('pointermove', function (event) {
        var rect = el.getBoundingClientRect();
        var dx = ((event.clientX - rect.left) / Math.max(1, rect.width) - 0.5) * 9;
        var dy = ((event.clientY - rect.top) / Math.max(1, rect.height) - 0.5) * 7;
        el.style.setProperty('--jtr-mag-x', dx.toFixed(2) + 'px');
        el.style.setProperty('--jtr-mag-y', dy.toFixed(2) + 'px');
      }, { passive: true });

      el.addEventListener('pointerleave', function () {
        el.style.setProperty('--jtr-mag-x', '0px');
        el.style.setProperty('--jtr-mag-y', '0px');
      });
    });
  }

  /* ---------- premium card tilt ---------- */
  function initTilt(scope) {
    if (!finePointer || reduceMotion) return;
    scope = scope || doc;

    toArray(scope.querySelectorAll('.jtr-tilt')).forEach(function (el) {
      if (el.getAttribute('data-jtr-tilt-ready') === 'true') return;
      el.setAttribute('data-jtr-tilt-ready', 'true');

      el.addEventListener('pointermove', function (event) {
        var rect = el.getBoundingClientRect();
        var x = ((event.clientX - rect.left) / Math.max(1, rect.width) - 0.5) * 2;
        var y = ((event.clientY - rect.top) / Math.max(1, rect.height) - 0.5) * 2;
        el.style.transform = 'perspective(1000px) rotateX(' + (-y * 2.25).toFixed(2) + 'deg) rotateY(' + (x * 2.25).toFixed(2) + 'deg) translateY(-4px)';
      }, { passive: true });

      el.addEventListener('pointerleave', function () {
        el.style.transform = '';
      });
    });
  }

  /* ---------- subtle image parallax ---------- */
  var scrollParallaxEls = [];

  function collectParallax(scope) {
    scope = scope || doc;
    toArray(scope.querySelectorAll('.jtr-feat .jtr-grow, .jtr-social-media .jtr-grow')).forEach(function (el) {
      if (el.getAttribute('data-jtr-scroll-parallax') === 'true') return;
      el.setAttribute('data-jtr-scroll-parallax', 'true');
      scrollParallaxEls.push(el);
    });
  }

  function updateImageParallax() {
    if (reduceMotion) return;
    var vh = win.innerHeight || doc.documentElement.clientHeight;

    scrollParallaxEls = scrollParallaxEls.filter(function (el) { return doc.documentElement.contains(el); });

    scrollParallaxEls.forEach(function (el) {
      var host = el.parentElement || el;
      var rect = host.getBoundingClientRect();
      if (rect.bottom < -80 || rect.top > vh + 80) return;
      var progress = ((rect.top + rect.height * 0.5) - vh * 0.5) / Math.max(vh, 1);
      el.style.setProperty('--jtr-scroll-y', (clamp(progress * -18, -10, 10)).toFixed(2) + 'px');
    });
  }

  /* ---------- hero mouse + scroll parallax, nav progress ---------- */
  var mouseEls = [];
  var hero = null;
  var progressBar = null;
  var nav = null;
  var targetMouseX = 0;
  var targetMouseY = 0;
  var mouseX = 0;
  var mouseY = 0;
  var lastScrollY = 0;
  var tickingStarted = false;

  function initGlobalMotion() {
    mouseEls = toArray(doc.querySelectorAll('[data-px-mouse]'));
    progressBar = doc.querySelector('[data-progress]');
    hero = doc.querySelector('#top') || doc.querySelector('.jtr-hero');
    nav = doc.querySelector('.jtr-nav');
    lastScrollY = win.scrollY || 0;

    if (finePointer && !reduceMotion && !doc.documentElement.hasAttribute('data-jtr-mouse-ready')) {
      doc.documentElement.setAttribute('data-jtr-mouse-ready', 'true');
      win.addEventListener('pointermove', function (event) {
        var target = hero || doc.body;
        var rect = target.getBoundingClientRect();
        targetMouseX = clamp(((event.clientX - rect.left) / Math.max(1, rect.width) - 0.5) * 2, -1, 1);
        targetMouseY = clamp(((event.clientY - rect.top) / Math.max(1, rect.height) - 0.5) * 2, -1, 1);
      }, { passive: true });
    }

    if (!tickingStarted) {
      tickingStarted = true;
      win.requestAnimationFrame(globalTick);
    }
  }

  function globalTick() {
    var scrollY = win.scrollY || doc.documentElement.scrollTop || 0;
    var vh = win.innerHeight || doc.documentElement.clientHeight;

    if (!reduceMotion) {
      mouseX += (targetMouseX - mouseX) * 0.065;
      mouseY += (targetMouseY - mouseY) * 0.065;

      mouseEls.forEach(function (el) {
        var strength = parseFloat(el.getAttribute('data-px-mouse')) || 12;
        var scrollOffset = el.classList.contains('jtr-hero-media') ? clamp(scrollY * 0.13, 0, 70) : 0;
        el.style.transform = 'translate3d(' + (mouseX * strength).toFixed(2) + 'px,' + ((mouseY * strength) + scrollOffset).toFixed(2) + 'px,0)';
      });

      updateImageParallax();
    }

    if (progressBar) {
      var docHeight = doc.documentElement.scrollHeight - vh;
      var progress = docHeight > 0 ? clamp(scrollY / docHeight, 0, 1) : 0;
      progressBar.style.transform = 'scaleX(' + progress.toFixed(4) + ')';
    }

    if (nav) {
      nav.classList.toggle('jtr-nav-scrolled', scrollY > 24);

      var openingMobileMenu = win.location.hash === '#jtr-mobile';
      if (!openingMobileMenu && scrollY > 150 && scrollY > lastScrollY + 3) {
        nav.classList.add('jtr-nav-hidden');
      } else if (scrollY < lastScrollY - 2 || scrollY < 90 || openingMobileMenu) {
        nav.classList.remove('jtr-nav-hidden');
      }
    }

    lastScrollY = scrollY;
    win.requestAnimationFrame(globalTick);
  }

  /* ---------- apparel/chassis filter only inside drop section ---------- */
  function initFilter(scope) {
    scope = scope || doc;
    var drop = scope.querySelector ? scope.querySelector('#drop') : null;
    if (!drop && scope !== doc) drop = doc.querySelector('#drop');
    if (!drop || drop.getAttribute('data-jtr-filter-ready') === 'true') return;
    drop.setAttribute('data-jtr-filter-ready', 'true');

    var chips = toArray(drop.querySelectorAll('[data-filter]'));
    var cards = toArray(drop.querySelectorAll('.jtr-pcard'));

    function applyFilter(filter) {
      cards.forEach(function (card) {
        var tags = (card.getAttribute('data-tags') || '').split(/\s+/);
        var match = filter === 'all' || String(filter).split(/\s+/).some(function (tag) {
          return tags.indexOf(tag) !== -1;
        });
        card.classList.toggle('jtr-dim', !match);
      });
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function (event) {
        var filter = chip.getAttribute('data-filter');
        if (!filter) return;
        event.preventDefault();

        var wasActive = chip.classList.contains('jtr-active') && filter !== 'all';
        chips.forEach(function (item) { item.classList.remove('jtr-active'); });

        if (wasActive) {
          var all = chips.filter(function (item) { return item.getAttribute('data-filter') === 'all'; })[0];
          if (all) all.classList.add('jtr-active');
          applyFilter('all');
        } else {
          chip.classList.add('jtr-active');
          applyFilter(filter);
        }

        win.scrollTo({
          top: drop.getBoundingClientRect().top + (win.scrollY || 0) - 80,
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      });
    });
  }

  /* ---------- click/tap product card photo to flick next image ---------- */
  function cycleCardImage(card) {
    if (!card) return false;
    var img = card.querySelector('.jtr-card-gallery-main');
    if (!img) return false;

    var a = img.getAttribute('data-image-a');
    var b = img.getAttribute('data-image-b');
    if (!a || !b) return false;

    var current = img.getAttribute('data-current') || 'a';
    var nextSrc = current === 'a' ? b : a;
    img.setAttribute('src', nextSrc);
    img.setAttribute('data-current', current === 'a' ? 'b' : 'a');
    return true;
  }

  function initCardCycleDelegation() {
    if (doc.documentElement.getAttribute('data-jtr-cycle-ready') === 'true') return;
    doc.documentElement.setAttribute('data-jtr-cycle-ready', 'true');

    doc.addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest('.jtr-card-cycle');
      if (!trigger) return;

      var gallery = trigger.closest('[data-jtr-card-gallery]');
      if (!gallery) return;

      if (cycleCardImage(gallery)) {
        event.preventDefault();
        event.stopPropagation();
      }
    });

    doc.addEventListener('keydown', function (event) {
      if ((event.key === 'Enter' || event.key === ' ') && event.target.classList.contains('jtr-card-cycle')) {
        event.preventDefault();
        event.target.click();
      }
    });
  }


  /* ---------- live social feeds ---------- */
  function socialText(value) {
    if (value === null || value === undefined) return '';
    return String(value);
  }

  function normaliseSocialItems(payload) {
    var items = [];
    if (Array.isArray(payload)) items = payload;
    else if (payload && Array.isArray(payload.posts)) items = payload.posts;
    else if (payload && Array.isArray(payload.videos)) items = payload.videos;
    else if (payload && Array.isArray(payload.data)) items = payload.data;

    return items.map(function (item) {
      item = item || {};
      return {
        url: item.url || item.share_url || item.permalink || item.embed_link || item.post_url || '',
        image: item.thumbnail_url || item.cover_image_url || item.cover_url || item.image_url || item.media_url || item.thumbnail || item.image || '',
        title: item.title || item.name || item.headline || '',
        caption: item.caption || item.description || item.text || item.desc || '',
        meta: item.meta || item.label || item.type || item.media_type || ''
      };
    }).filter(function (item) {
      return item.url || item.image || item.title || item.caption;
    });
  }

  function hydrateSocialCard(card, item, platform) {
    if (!card || !item) return;

    if (item.url) card.setAttribute('href', item.url);

    var media = card.querySelector('.jtr-social-media');
    var image = card.querySelector('[data-jtr-social-image]');
    var placeholder = card.querySelector('[data-jtr-social-placeholder]');

    if (item.image && media) {
      if (!image) {
        image = doc.createElement('img');
        image.className = 'jtr-grow';
        image.setAttribute('data-jtr-social-image', '');
        media.insertBefore(image, media.firstChild);
      }
      image.src = item.image;
      image.alt = item.title || item.caption || ('Latest ' + platform + ' post');
      if (placeholder) placeholder.style.display = 'none';
    }

    var meta = card.querySelector('[data-jtr-social-meta]');
    var title = card.querySelector('[data-jtr-social-title]');
    var caption = card.querySelector('[data-jtr-social-caption]');

    if (meta) meta.textContent = item.meta || (platform === 'tiktok' ? 'Latest TikTok' : 'Latest Instagram');
    if (title && item.title) title.textContent = socialText(item.title);
    if (caption && item.caption) caption.textContent = socialText(item.caption);

    card.classList.add('jtr-social-live');
    card.setAttribute('data-live', 'true');
  }

  function loadSocialPlatform(section, platform, endpoint) {
    if (!endpoint || !section) return Promise.resolve();

    var cards = toArray(section.querySelectorAll('[data-jtr-social-card][data-platform="' + platform + '"]'));
    if (!cards.length) return Promise.resolve();

    return win.fetch(endpoint, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      headers: { 'Accept': 'application/json' },
      cache: 'no-store'
    }).then(function (response) {
      if (!response.ok) throw new Error('Social feed HTTP ' + response.status);
      return response.json();
    }).then(function (payload) {
      var items = normaliseSocialItems(payload);
      items.slice(0, cards.length).forEach(function (item, index) {
        hydrateSocialCard(cards[index], item, platform);
      });
      section.setAttribute('data-' + platform + '-live', items.length ? 'true' : 'empty');
    }).catch(function (error) {
      section.setAttribute('data-' + platform + '-live', 'fallback');
      if (win.console && console.warn) console.warn('[JTR] ' + platform + ' live feed unavailable; using manual fallback.', error);
    });
  }

  function refreshSocialSection(section) {
    if (!section) return;
    var instagram = section.getAttribute('data-instagram-feed') || '';
    var tiktok = section.getAttribute('data-tiktok-feed') || '';
    loadSocialPlatform(section, 'instagram', instagram);
    loadSocialPlatform(section, 'tiktok', tiktok);
  }

  function initLiveSocial(scope) {
    scope = scope || doc;
    var sections = toArray(scope.querySelectorAll ? scope.querySelectorAll('[data-jtr-live-social="true"]') : []);
    if (scope.matches && scope.matches('[data-jtr-live-social="true"]')) sections.unshift(scope);

    sections.forEach(function (section) {
      if (section.getAttribute('data-jtr-live-ready') === 'true') return;
      section.setAttribute('data-jtr-live-ready', 'true');
      refreshSocialSection(section);

      var minutes = parseInt(section.getAttribute('data-refresh-minutes') || '15', 10);
      if (!isFinite(minutes) || minutes < 5) minutes = 15;
      var timer = win.setInterval(function () { refreshSocialSection(section); }, minutes * 60 * 1000);
      section._jtrSocialTimer = timer;
    });
  }

  /* ---------- initialise / Shopify section editor ---------- */
  function initScope(scope) {
    initReveals(scope);
    initReactiveCards(scope);
    initMagnetic(scope);
    initTilt(scope);
    collectParallax(scope);
    initFilter(scope);
    initLiveSocial(scope);
  }

  function init() {
    initScope(doc);
    initCardCycleDelegation();
    initPointerGlow();
    initGlobalMotion();
    markReady();

    doc.addEventListener('shopify:section:load', function (event) {
      initScope(event.target || doc);
      initGlobalMotion();
    });
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
